import { isCameraStreamLogsEnabled } from '../config';
import { visibilityTracker } from './visibilityTracker';
import { nackStatsLogger } from 'services/CameraStream/KurentoRtspSource/NackStatsLogger';

const FAST_CYCLE_MS = 4000;
const SLOW_CYCLE_MS = 30000;
const BATCH = 5;

export default class FreezeDetector {
    #worker;

    #visibleSources = new Map(); // cameraId -> source
    #offscreenSources = new Map(); // cameraId -> source
    #visibleOrder = []; // cameraIds (round-robin order)
    #offscreenOrder = [];
    #visibleIdx = 0;
    #offscreenIdx = 0;

    #receivers = new Map(); // cameraId -> RTCRtpReceiver (cached)

    #running = false;
    #fastTimeoutId;
    #slowTimeoutId;
    #unsubscribeVisibility;

    constructor() {
        if (typeof Worker !== 'function') {
            this.#worker = null;
            this.#log('Worker not available; freeze detection disabled', 'warn');
            return;
        }
        this.#worker = new Worker(new URL('./freezeDetectorWorker.js', import.meta.url), { type: 'module' });
        this.#worker.onmessage = this.#onWorkerMessage;
        this.#worker.onerror = (err) => this.#log(`worker error: ${err?.message || err}`, 'error');
    }

    start() {
        if (this.#running) return;
        this.#running = true;
        this.#unsubscribeVisibility = visibilityTracker.subscribe(this.#onVisibilityChange);
        this.#scheduleFast(0);
        this.#scheduleSlow(0);
        this.#log('started');
    }

    stop() {
        this.#running = false;
        clearTimeout(this.#fastTimeoutId);
        clearTimeout(this.#slowTimeoutId);
        this.#fastTimeoutId = undefined;
        this.#slowTimeoutId = undefined;
        this.#unsubscribeVisibility?.();
        this.#unsubscribeVisibility = null;
        this.#postToWorker({ type: 'reset' });
        this.#log('stopped');
    }

    register(source) {
        if (!source || source.cameraId == null) return;
        const cameraId = source.cameraId;
        if (typeof source.getPeerConnection !== 'function') return; // skip non-Kurento sources
        if (this.#visibleSources.has(cameraId) || this.#offscreenSources.has(cameraId)) return;
        const pool = visibilityTracker.isVisible(cameraId) ? 'visible' : 'offscreen';
        this.#addToPool(cameraId, source, pool);
        this.#log(`registered cameraId=${cameraId} pool=${pool}`);
    }

    unregister(cameraId) {
        if (cameraId == null) return;
        const removed = this.#removeFromPool(cameraId);
        this.#receivers.delete(cameraId);
        if (removed) {
            this.#postToWorker({ type: 'remove', cameraId });
            this.#log(`unregistered cameraId=${cameraId}`);
        }
    }

    // Backwards-compat alias used by CameraStreamManager during transitions.
    notifyRemoved(cameraId) {
        this.unregister(cameraId);
    }

    #onVisibilityChange = (cameraId, isVisible) => {
        const source = this.#visibleSources.get(cameraId) || this.#offscreenSources.get(cameraId);
        if (!source) return;
        const target = isVisible ? 'visible' : 'offscreen';
        const inVisible = this.#visibleSources.has(cameraId);
        const inOffscreen = this.#offscreenSources.has(cameraId);
        if ((target === 'visible' && inVisible) || (target === 'offscreen' && inOffscreen)) return;
        this.#removeFromPool(cameraId);
        this.#addToPool(cameraId, source, target);
        this.#log(`cameraId=${cameraId} moved to pool=${target}`);
    };

    #addToPool(cameraId, source, pool) {
        if (pool === 'visible') {
            this.#visibleSources.set(cameraId, source);
            this.#visibleOrder.push(cameraId);
        } else {
            this.#offscreenSources.set(cameraId, source);
            this.#offscreenOrder.push(cameraId);
        }
    }

    #removeFromPool(cameraId) {
        let removed = false;
        if (this.#visibleSources.delete(cameraId)) {
            const i = this.#visibleOrder.indexOf(cameraId);
            if (i >= 0) {
                this.#visibleOrder.splice(i, 1);
                if (this.#visibleIdx > i) this.#visibleIdx -= 1;
            }
            removed = true;
        }
        if (this.#offscreenSources.delete(cameraId)) {
            const i = this.#offscreenOrder.indexOf(cameraId);
            if (i >= 0) {
                this.#offscreenOrder.splice(i, 1);
                if (this.#offscreenIdx > i) this.#offscreenIdx -= 1;
            }
            removed = true;
        }
        return removed;
    }

    #scheduleFast(delay) {
        if (!this.#running) return;
        this.#fastTimeoutId = setTimeout(this.#tickFast, delay);
    }

    #scheduleSlow(delay) {
        if (!this.#running) return;
        this.#slowTimeoutId = setTimeout(this.#tickSlow, delay);
    }

    #tickFast = () => this.#tickPool('visible');
    #tickSlow = () => this.#tickPool('offscreen');

    #tickPool(pool) {
        if (!this.#running) return;

        const order = pool === 'visible' ? this.#visibleOrder : this.#offscreenOrder;
        const sourceMap = pool === 'visible' ? this.#visibleSources : this.#offscreenSources;
        const cycleMs = pool === 'visible' ? FAST_CYCLE_MS : SLOW_CYCLE_MS;
        const schedule = pool === 'visible' ? (d) => this.#scheduleFast(d) : (d) => this.#scheduleSlow(d);

        const n = order.length;
        if (!n) {
            schedule(cycleMs);
            return;
        }

        const batch = Math.min(BATCH, n);
        const ticksPerCycle = Math.ceil(n / batch);
        const period = Math.max(50, Math.floor(cycleMs / ticksPerCycle));

        // Schedule the next tick BEFORE awaiting any getStats() call so the
        // loop cadence is independent of getStats() latency.
        schedule(period);

        let idx = pool === 'visible' ? this.#visibleIdx : this.#offscreenIdx;
        if (idx >= n) idx = 0;
        const picked = [];
        for (let i = 0; i < batch; i++) {
            const cameraId = order[(idx + i) % n];
            const source = sourceMap.get(cameraId);
            if (source) picked.push(source);
        }
        const newIdx = (idx + batch) % n;
        if (pool === 'visible') this.#visibleIdx = newIdx;
        else this.#offscreenIdx = newIdx;

        // Fire-and-forget batch sample.
        this.#sampleBatch(picked).catch((err) => this.#log(`batch sample error: ${err?.message || err}`, 'error'));
    }

    async #sampleBatch(sources) {
        if (!sources.length) return;
        const t = Date.now();
        const settled = await Promise.all(sources.map((s) => this.#sampleOne(s, t)));
        const samples = settled.filter(Boolean);
        if (!samples.length) return;
        this.#postToWorker({ type: 'samples', samples });
    }

    async #sampleOne(source, t) {
        try {
            // Skip deactivated streams (STOP_CAMERA_STREAM clears activeStreamType
            // while leaving the peer connection alive).
            if (!source?.activeStreamType) return null;
            const pc = source.getPeerConnection?.();
            if (!pc || typeof pc.getStats !== 'function') return null;

            const track = this.#getReceiverTrack(source.cameraId, pc);
            const report = await pc.getStats(track || undefined);

            let inbound = null;
            for (const s of report.values()) {
                if (s && s.type === 'inbound-rtp' && s.kind === 'video') {
                    nackStatsLogger.observe(source, pc, s);
                    inbound = {
                        ssrc: s.ssrc,
                        framesDecoded: s.framesDecoded,
                        framesReceived: s.framesReceived,
                        framesDropped: s.framesDropped,
                        framesPerSecond: s.framesPerSecond,
                        bytesReceived: s.bytesReceived,
                        timestamp: s.timestamp,
                    };
                    break;
                }
            }
            return { cameraId: source.cameraId, inbound, t };
        } catch (err) {
            this.#log(`sample error cameraId=${source?.cameraId}: ${err?.message || err}`, 'error');
            // Drop a stale receiver so it gets re-resolved on the next tick.
            if (source?.cameraId != null) this.#receivers.delete(source.cameraId);
            return null;
        }
    }

    #getReceiverTrack(cameraId, pc) {
        const cached = this.#receivers.get(cameraId);
        if (cached && cached.track && cached.track.readyState === 'live') {
            // Best-effort verification that the cached receiver still belongs to this pc.
            const receivers = pc.getReceivers?.();
            if (!receivers || receivers.includes(cached)) return cached.track;
        }
        const receivers = pc.getReceivers?.() || [];
        const fresh = receivers.find((r) => r.track && r.track.kind === 'video');
        if (fresh) this.#receivers.set(cameraId, fresh);
        else this.#receivers.delete(cameraId);
        return fresh?.track || null;
    }

    #onWorkerMessage = (ev) => {
        const msg = ev.data;
        if (!msg || msg.type !== 'verdict') return;
        const source = this.#visibleSources.get(msg.cameraId) || this.#offscreenSources.get(msg.cameraId);
        if (!source) return;
        try {
            source.onFreezeDetectorVerdict?.(!!msg.frozen, msg.reason);
        } catch (err) {
            this.#log(`verdict dispatch error cameraId=${msg.cameraId}: ${err?.message || err}`, 'error');
        }
    };

    #postToWorker(message) {
        this.#worker?.postMessage(message);
    }

    #log(message, mode = 'log') {
        if (!isCameraStreamLogsEnabled(mode)) return;
        console[mode]?.(`[FreezeDetector] ${message}`);
    }
}
